/**
 * src/components/sidebar/FriendsPanel.tsx
 *
 * The Friends view that swaps in place of the DM list in the context
 * sidebar. Search, add-by-username, and inbound/outbound request handling
 * all live here so the sidebar never has to navigate away.
 *
 * Data comes from useFriends (public.friends, migration 20260918000000).
 */
import React, { useMemo, useState } from 'react';
import { Search, UserPlus, Check, X, MessageSquare, Users, Loader2 } from 'lucide-react';
import type { FriendProfile } from '../../hooks/useFriends';

interface FriendsPanelProps {
  friends: FriendProfile[];
  incoming: FriendProfile[];
  outgoing: FriendProfile[];
  loading: boolean;
  onAccept: (userId: string) => void | Promise<void>;
  onRemove: (userId: string) => void | Promise<void>;
  onAddByUsername: (username: string) => Promise<{ ok: boolean; message: string }>;
  onOpenDM: (userId: string) => void;
  onUserContextMenu?: (e: React.MouseEvent, userId: string) => void;
}

function Avatar({ profile }: { profile: FriendProfile }) {
  if (profile.avatar) {
    return <img src={profile.avatar} alt="" className="w-9 h-9 rounded-xl object-cover border border-white/5 shrink-0" />;
  }
  return (
    <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-black text-white/40 shrink-0">
      {profile.name.slice(0, 2).toUpperCase()}
    </div>
  );
}

export default function FriendsPanel({
  friends,
  incoming,
  outgoing,
  loading,
  onAccept,
  onRemove,
  onAddByUsername,
  onOpenDM,
  onUserContextMenu,
}: FriendsPanelProps) {
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addResult, setAddResult] = useState<{ ok: boolean; message: string } | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return friends;
    return friends.filter(f => f.name.toLowerCase().includes(q));
  }, [friends, search]);

  const submitAdd = async () => {
    if (addBusy) return;
    setAddBusy(true);
    setAddResult(null);
    const result = await onAddByUsername(addValue);
    setAddResult(result);
    if (result.ok) setAddValue('');
    setAddBusy(false);
  };

  return (
    <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2 no-scrollbar">
      {/* Search + add */}
      <div className="flex items-center gap-2 mb-1">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search friends..."
            className="w-full bg-black/40 border border-white/10 rounded-xl pl-7 pr-2 py-1.5 text-[11px] text-white outline-none focus:border-[var(--neon)] transition-all"
          />
        </div>
        <button
          onClick={() => { setShowAdd(v => !v); setAddResult(null); }}
          title="Add friend by username"
          className={`p-1.5 rounded-xl border transition-all shrink-0 ${
            showAdd
              ? 'bg-[var(--neon)]/20 border-[var(--neon)] text-[var(--neon)]'
              : 'bg-white/5 border-white/10 text-white/50 hover:text-white hover:bg-white/10'
          }`}
        >
          <UserPlus size={14} />
        </button>
      </div>

      {showAdd && (
        <div className="p-2.5 rounded-xl bg-black/40 border border-white/10 flex flex-col gap-2 mb-1">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={addValue}
              onChange={e => setAddValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void submitAdd(); }}
              placeholder="Enter a username"
              autoFocus
              className="flex-1 bg-black/50 border border-white/10 rounded-lg px-2 py-1.5 text-[11px] text-white outline-none focus:border-[var(--neon)] transition-all"
            />
            <button
              onClick={() => void submitAdd()}
              disabled={addBusy || !addValue.trim()}
              className="px-2.5 py-1.5 rounded-lg bg-[var(--neon)] text-black text-[9px] font-black uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed shrink-0 flex items-center gap-1"
            >
              {addBusy ? <Loader2 size={11} className="animate-spin" /> : 'Send'}
            </button>
          </div>
          {addResult && (
            <p className={`text-[10px] leading-snug ${addResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>
              {addResult.message}
            </p>
          )}
        </div>
      )}

      {/* Incoming requests — first, because they need action */}
      {incoming.length > 0 && (
        <>
          <div className="px-1 pt-1 flex items-center gap-1.5">
            <span className="text-[9px] font-black text-[var(--neon)] uppercase tracking-[0.2em]">
              Requests
            </span>
            <span className="px-1.5 rounded-full bg-[var(--neon)] text-black text-[9px] font-black">
              {incoming.length}
            </span>
          </div>
          {incoming.map(p => (
            <div key={p.id} className="flex items-center gap-2.5 p-2 rounded-2xl bg-[var(--neon)]/5 border border-[var(--neon)]/20">
              <Avatar profile={p} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-white truncate">{p.name}</div>
                <div className="text-[10px] text-white/30 uppercase tracking-tighter">wants to connect</div>
              </div>
              <button
                onClick={() => void onAccept(p.id)}
                title="Accept"
                className="p-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/25 transition-all shrink-0"
              >
                <Check size={13} />
              </button>
              <button
                onClick={() => void onRemove(p.id)}
                title="Decline"
                className="p-1.5 rounded-lg bg-red-500/10 border border-red-400/30 text-red-400 hover:bg-red-500/20 transition-all shrink-0"
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </>
      )}

      {/* Outgoing — informational, cancellable */}
      {outgoing.length > 0 && (
        <>
          <div className="px-1 pt-2">
            <span className="text-[9px] font-black text-white/20 uppercase tracking-[0.2em]">
              Pending ({outgoing.length})
            </span>
          </div>
          {outgoing.map(p => (
            <div key={p.id} className="flex items-center gap-2.5 p-2 rounded-2xl opacity-60">
              <Avatar profile={p} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black text-white truncate">{p.name}</div>
                <div className="text-[10px] text-white/30 uppercase tracking-tighter">request sent</div>
              </div>
              <button
                onClick={() => void onRemove(p.id)}
                title="Cancel request"
                className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-white/10 transition-all shrink-0"
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </>
      )}

      {/* Accepted friends */}
      <div className="px-1 pt-2">
        <span className="text-[9px] font-black text-white/20 uppercase tracking-[0.2em]">
          All Friends {friends.length > 0 && `(${friends.length})`}
        </span>
      </div>

      {loading ? (
        Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-2 rounded-2xl">
            <div className="w-9 h-9 rounded-xl bg-white/5 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-white/10 rounded animate-pulse w-24" />
              <div className="h-2.5 bg-white/5 rounded animate-pulse w-32" />
            </div>
          </div>
        ))
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-center px-4">
          <Users size={26} className="text-white/10" />
          <p className="text-[10px] text-white/30 uppercase tracking-widest leading-relaxed">
            {search
              ? 'No friends match that search'
              : friends.length === 0
                ? 'No friends yet — add someone by username'
                : ''}
          </p>
        </div>
      ) : (
        filtered.map(p => (
          <div key={p.id} className="relative group">
            <button
              onClick={() => onOpenDM(p.id)}
              onContextMenu={e => onUserContextMenu?.(e, p.id)}
              className="w-full flex items-center gap-3 p-2 rounded-2xl hover:bg-white/5 transition-all"
            >
              <Avatar profile={p} />
              <div className="flex-1 min-w-0 text-left">
                <div className="text-xs font-black text-white group-hover:text-[var(--neon)] transition-colors truncate">
                  {p.name}
                </div>
                <div className="text-[10px] text-white/30 truncate uppercase tracking-tighter">
                  {p.presence || 'Offline'}
                </div>
              </div>
            </button>
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={e => { e.stopPropagation(); onOpenDM(p.id); }}
                title="Message"
                className="p-1.5 rounded-lg text-white/40 hover:text-[var(--neon)] hover:bg-white/10 transition-all"
              >
                <MessageSquare size={12} />
              </button>
              <button
                onClick={e => { e.stopPropagation(); void onRemove(p.id); }}
                title="Remove friend"
                className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-white/10 transition-all"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
